/**
 * Enhanced HTTP Client with session-based authentication
 * Business context now comes automatically from Redis session
 */

import { signalDataChanged } from '../utils/dataChangeSignal';

/**
 * Every successful write announces itself, so screens can refresh on the change
 * instead of asking the database on a timer whether anything changed. It is
 * raised here rather than at each call site because the server also records
 * changes as a side effect of other requests (a merge cleaning up unlinked
 * ingredients, for one), which no hand-placed call would ever catch.
 *
 * A refused write (403 comes back as `{ success: false }` rather than throwing)
 * changed nothing, so it says nothing.
 */
const announceWrite = (endpoint, responseData) => {
  if (responseData && responseData.success === false) return responseData;
  if (responseData && responseData.tokenRefreshed) return responseData; // the retry will announce
  signalDataChanged(endpoint);
  return responseData;
};

const normalizeApiBaseUrl = (baseUrl) => {
  const raw = (baseUrl || '').trim();
  if (!raw) return '';
  const noTrailingSlash = raw.replace(/\/+$/, '');
  return noTrailingSlash.endsWith('/api') ? noTrailingSlash : `${noTrailingSlash}/api`;
};

// Prefer CRA dev proxy in development to avoid CORS and port/IP mismatch.
// Backend routes are mounted under /api (e.g. /api/auth/signin, /api/health/db-status).
const envBase = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '';
const API_BASE_URL = normalizeApiBaseUrl(envBase) || '/api';

// Get auth token from localStorage
const getAuthToken = () => {
  return localStorage.getItem('token') || localStorage.getItem('authToken');
};

// Get standard headers with authentication
const getHeaders = () => {
  const headers = {
    'Content-Type': 'application/json',
  };

  // Try multiple token sources
  const token = getAuthToken();
  if (token) {
    // Validate token format and expiration
    try {
      const parts = token.split('.');
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1]));
        // Check if token is expired
        const now = Date.now() / 1000;
        if (payload.exp && payload.exp < now) {
          console.warn('⚠️ Token expired, removing...');
          localStorage.removeItem('token');
          localStorage.removeItem('authToken');
        } else {
          headers['Authorization'] = `Bearer ${token}`;
        }
      } else {
        // Invalid token format
        console.warn('⚠️ Invalid token format, removing...');
        localStorage.removeItem('token');
        localStorage.removeItem('authToken');
      }
    } catch (e) {
      console.warn('⚠️ Error validating token, removing...', e);
      localStorage.removeItem('token');
      localStorage.removeItem('authToken');
    }
  } else {
    console.warn('⚠️ No token found in localStorage');
  }

  // Include business ID header from localStorage for proper business context
  const businessId = localStorage.getItem('businessId');
  if (businessId) {
    headers['X-Business-Id'] = businessId;
  }

  return headers;
};

// HTTP client methods
const http = {
  // Second parameter is backward compatible in the same way `post`'s third is: existing
  // callers pass nothing, the token-refresh recursion passes the boolean `true`, and it
  // may also be an options object `{ signal, hasRetried }` so a caller can abort an
  // in-flight GET. The Sales Report needs this: without a signal there is no way to stop
  // waiting on a report request that never returns, which is what left the screen showing
  // "Loading sales data..." indefinitely.
  async get(endpoint, opts = false) {
    const { signal, hasRetried = false } = (opts && typeof opts === 'object')
      ? opts
      : { signal: undefined, hasRetried: Boolean(opts) };

    const fetchOptions = {
      method: 'GET',
      headers: getHeaders(),
    };
    if (signal) fetchOptions.signal = signal;

    const response = await fetch(`${API_BASE_URL}${endpoint}`, fetchOptions);
    const data = await this.handleResponse(response);

    if (data && data.tokenRefreshed && !hasRetried) {
      return this.get(endpoint, { signal, hasRetried: true });
    }

    return data;
  },

  // Third parameter is backward compatible: existing callers pass nothing, and the
  // token-refresh recursion passes the boolean `true`. It may now also be an options
  // object `{ signal, hasRetried }` so a caller can abort an in-flight request
  // (T1.8: the wastage form aborts on timeout instead of leaving the POST running
  // while the UI resets — a resumed retry must not race a request it stopped watching).
  async post(endpoint, data, optionsOrRetried = false) {
    const opts = typeof optionsOrRetried === 'object' && optionsOrRetried !== null
      ? optionsOrRetried
      : { hasRetried: Boolean(optionsOrRetried) };
    const { signal, hasRetried = false } = opts;

    const fetchOptions = {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    };
    if (signal) fetchOptions.signal = signal;

    const response = await fetch(`${API_BASE_URL}${endpoint}`, fetchOptions);
    const responseData = await this.handleResponse(response);

    if (responseData && responseData.tokenRefreshed && !hasRetried) {
      return this.post(endpoint, data, { signal, hasRetried: true });
    }

    return announceWrite(endpoint, responseData);
  },

  async put(endpoint, data, hasRetried = false) {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    const responseData = await this.handleResponse(response);

    if (responseData && responseData.tokenRefreshed && !hasRetried) {
      return this.put(endpoint, data, true);
    }

    return announceWrite(endpoint, responseData);
  },

  // PATCH — a partial update, for endpoints that change a few named fields
  // rather than replacing a resource (recording a spot-check result, confirming
  // a preparation's batch size). Mirrors put(), including the token-refresh
  // retry, so a refreshed session does not silently drop the write.
  async patch(endpoint, data, hasRetried = false) {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    const responseData = await this.handleResponse(response);

    if (responseData && responseData.tokenRefreshed && !hasRetried) {
      return this.patch(endpoint, data, true);
    }

    return announceWrite(endpoint, responseData);
  },

  async delete(endpoint, body, hasRetried = false) {
    const fetchOptions = {
      method: 'DELETE',
      headers: getHeaders(),
    };
    // Support optional JSON body (e.g. for bulk-delete operations)
    if (body !== undefined && body !== null && typeof body === 'object' && !Array.isArray(body)) {
      fetchOptions.body = JSON.stringify(body);
    }
    const response = await fetch(`${API_BASE_URL}${endpoint}`, fetchOptions);
    const data = await this.handleResponse(response);

    if (data && data.tokenRefreshed && !hasRetried) {
      return this.delete(endpoint, body, true);
    }

    return announceWrite(endpoint, data);
  },

  // Handle form data (for file uploads) - main method
  async postFormData(endpoint, formData, hasRetried = false) {
    const token = getAuthToken();
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Include business ID header for proper business context
    const businessId = localStorage.getItem('businessId');
    if (businessId) {
      headers['X-Business-Id'] = businessId;
    }
    
    // Don't set Content-Type for FormData - browser will set it with boundary

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers,
      body: formData,
    });
    const responseData = await this.handleResponse(response);

    if (responseData && responseData.tokenRefreshed && !hasRetried) {
      return this.postFormData(endpoint, formData, true);
    }

    return announceWrite(endpoint, responseData);
  },

  // Alias for backward compatibility
  async postForm(endpoint, formData, options = {}) {
    return this.postFormData(endpoint, formData);
  },

  async handleResponse(response) {
    if (response.status === 401) {
      
      // Development bypass - allow continuing without proper auth
      const isDevelopment = process.env.NODE_ENV === 'development' || 
                           window.location.hostname === 'localhost' || 
                           window.location.hostname.match(/^(172|192|10)\./);
      
      if (isDevelopment) {
        console.warn('⚠️ Development mode: 401 Unauthorized - Please log in again at /login');
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('authToken');
        
        // Set a flag in localStorage for the login page to show message
        localStorage.setItem('sessionExpired', 'true');
        window.location.href = '/login';
        throw new Error('Authentication required - please log in');
      }
      
      // Try to refresh token (production path)
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        // Call refresh endpoint
        const refreshResponse = await fetch(`${API_BASE_URL}/auth/refresh`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refreshToken }),
        });

        if (refreshResponse.ok) {
          const refreshData = await refreshResponse.json();
          
          if (refreshData.token) {
            // Store new access token
            localStorage.setItem('token', refreshData.token);
            localStorage.setItem('user', JSON.stringify(refreshData.user));
            
            // Return special flag to indicate retry needed
            return { 
              success: false, 
              error: 'Token refreshed, please retry',
              tokenRefreshed: true
            };
          }
        }
        
        throw new Error('Token refresh failed');
        
      } catch (refreshError) {
        console.error('❌ Token refresh failed:', refreshError);
        
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        
        // Set a flag in localStorage for the login page to show message
        localStorage.setItem('sessionExpired', 'true');
        window.location.href = '/login';
        throw new Error('Authentication required');
      }
    }
    
    if (response.status === 403) {
      // Permission denied - don't clear token
      let errorData;
      try {
        errorData = await response.json();
      } catch (e) {
        errorData = { error: 'Access denied' };
      }
      // The code travels too, so a caller can tell "needs a manager" from any other refusal.
      return { success: false, error: errorData.error || 'Access denied', code: errorData.code };
    }

    // Safely get content-type header
    const contentTypeHeader = response.headers.get('content-type');
    const contentType = typeof contentTypeHeader === 'string' ? contentTypeHeader : '';
    let data;
    
    // Parse response based on content-type
    try {
      if (typeof contentType === 'string' && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }
    } catch (parseError) {
      console.error('Failed to parse response:', parseError);
      data = await response.text();
    }

    if (!response.ok) {
      console.error('❌ HTTP Client Error:', {
        status: response.status,
        statusText: response.statusText,
        url: response.url,
        data: data
      });
      
      const error = new Error(data.error || data.message || data.details || `HTTP ${response.status}: ${response.statusText}`);
      error.status = response.status;
      error.statusText = response.statusText;
      error.data = data;
      error.url = response.url;
      
      // Log the full error before throwing
      console.error('❌ Throwing error:', {
        message: error.message,
        status: error.status,
        data: error.data
      });
      
      throw error;
    }

    return data;
  }
};

export default http;